import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { districtAPI } from '../../services/api';
import SuccessModal from '../Common/SuccessModal';
import ErrorModal from '../Common/ErrorModal';
import { AdminPage, StateBlock } from '../Common/admin';

const DistrictEdit = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    districtCode: '',
    districtNameAr: '',
    districtNameEng: '',
    cityId: '',
    isActive: true,
  });
  const [cities, setCities] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const loadCities = useCallback(async () => {
    try {
      const response = await districtAPI.getCities();
      setCities(response.data || []);
    } catch (err) {
      console.error('Failed to load cities:', err);
    }
  }, []);

  const loadDistrict = useCallback(async () => {
    try {
      setLoadingData(true);
      const response = await districtAPI.getById(id);
      const data = response.data;
      setFormData({
        districtCode: data.districtCode || '',
        districtNameAr: data.districtNameAr || '',
        districtNameEng: data.districtNameEng || '',
        cityId: data.cityId || '',
        isActive: !!data.isActive,
      });
    } catch (err) {
      setErrorMessage(t('district.edit.messages.loadError'));
      setShowErrorModal(true);
    } finally {
      setLoadingData(false);
    }
  }, [id, t]);

  useEffect(() => {
    loadCities();
    loadDistrict();
  }, [loadCities, loadDistrict]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.districtCode.trim()) {
      newErrors.districtCode = t('district.edit.errors.districtCodeRequired');
    }
    if (!formData.districtNameAr.trim()) {
      newErrors.districtNameAr = t('district.edit.errors.nameArRequired');
    }
    if (!formData.districtNameEng.trim()) {
      newErrors.districtNameEng = t('district.edit.errors.nameEngRequired');
    }
    if (!formData.cityId) {
      newErrors.cityId = t('district.edit.errors.cityRequired');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      await districtAPI.update(id, { ...formData, cityId: parseInt(formData.cityId) });
      setShowSuccessModal(true);
      setTimeout(() => navigate('/district'), 1000);
    } catch (err) {
      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
      } else {
        setErrorMessage(t('district.edit.messages.updateError'));
        setShowErrorModal(true);
      }
      setLoading(false);
    }
  };

  const textField = (name, label, maxLength) => (
    <div className="ad-form-field">
      <label className="ad-label ad-label-required" htmlFor={name}>
        {label}
      </label>
      <input
        id={name}
        type="text"
        name={name}
        className={`ad-input ${errors[name] ? 'ad-input-error' : ''}`}
        value={formData[name]}
        onChange={handleChange}
        maxLength={maxLength}
      />
      {errors[name] && <span className="ad-error-text">{errors[name]}</span>}
    </div>
  );

  const backButton = (
    <button type="button" className="ad-btn" onClick={() => navigate('/district')}>
      <i className="fas fa-arrow-right"></i>
      {t('district.edit.backToList')}
    </button>
  );

  if (loadingData) {
    return (
      <AdminPage title={t('district.edit.title')} headerActions={backButton}>
        <StateBlock icon="fa-hourglass-half" title={t('common.loading')} />
      </AdminPage>
    );
  }

  return (
    <AdminPage title={t('district.edit.title')} headerActions={backButton}>
      <form onSubmit={handleSubmit}>
        <div className="ad-form">
          <div className="ad-form-grid">
            {textField('districtCode', t('district.edit.fields.districtCode'), 20)}
            {textField('districtNameAr', t('district.edit.fields.nameAr'), 100)}
            {textField('districtNameEng', t('district.edit.fields.nameEng'), 100)}

            <div className="ad-form-field">
              <label className="ad-label ad-label-required" htmlFor="cityId">
                {t('district.edit.fields.city')}
              </label>
              <select
                id="cityId"
                name="cityId"
                className={`ad-select ${errors.cityId ? 'ad-input-error' : ''}`}
                value={formData.cityId}
                onChange={handleChange}
              >
                <option value="">{t('district.edit.fields.city')}</option>
                {cities.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.cityNameEng}
                  </option>
                ))}
              </select>
              {errors.cityId && (
                <span className="ad-error-text">{errors.cityId}</span>
              )}
            </div>
          </div>

          <div className="ad-form-section">
            <div className="ad-check-row">
              <input
                id="isActive"
                type="checkbox"
                name="isActive"
                className="ad-checkbox"
                checked={formData.isActive}
                onChange={handleChange}
              />
              <label htmlFor="isActive">{t('district.edit.fields.isActive')}</label>
            </div>
          </div>
        </div>

        <div className="ad-form-foot">
          <button type="button" className="ad-btn" onClick={() => navigate('/district')}>
            {t('common.cancel')}
          </button>
          <button type="submit" className="ad-btn ad-btn-primary" disabled={loading}>
            {loading ? t('common.loading') : t('district.edit.save')}
          </button>
        </div>
      </form>

      <SuccessModal
        show={showSuccessModal}
        message={t('district.edit.messages.updateSuccess')}
        onClose={() => setShowSuccessModal(false)}
      />
      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
      />
    </AdminPage>
  );
};

export default DistrictEdit;
